/**
 * Simulated AI practice-test generator.
 *
 * For the demo, "AI generation" pulls questions from the existing
 * curated bank (`questions.js`), filters by the requested type, removes
 * HTML formatting (the editor uses plain-text inputs), and returns a list
 * shaped for the assessment store's `IMPORT_QUESTIONS` mutation.
 *
 * Real-world hook-up would replace `pickFromBank` with an API call to
 * an LLM endpoint and a JSON-mode response constrained by a schema.
 */

import { QUESTION_BANKS } from './questions.js';

/** Pull every question from every bank into a single flat array. */
function flatBank() {
  const out = [];
  for (const cat of Object.keys(QUESTION_BANKS)) {
    for (const q of QUESTION_BANKS[cat]) out.push({ ...q, _cat: cat });
  }
  return out;
}

/** Strip simple HTML tags and decode the most common entities. */
function stripHtml(s) {
  if (!s) return '';
  return String(s)
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

/** Fisher-Yates with optional seed for repeatable picks across calls. */
function shuffle(arr, seed) {
  const out = arr.slice();
  let s = seed || Date.now();
  for (let i = out.length - 1; i > 0; i--) {
    s = (s * 9301 + 49297) % 233280;
    const j = Math.floor((s / 233280) * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/** Map a bank entry → assessment-shaped question. */
function toAssessmentQuestion(q) {
  const correctSet = Array.isArray(q.correct) ? q.correct : [q.correct];
  return {
    type: q.type === 'multi' ? 'multi' : 'single',
    prompt: stripHtml(q.prompt),
    explanation: stripHtml(q.explanation),
    points: 1,
    tags: Array.isArray(q.tags) ? q.tags.slice() : [],
    options: q.answers.map((text, i) => ({
      text: stripHtml(text),
      correct: correctSet.includes(i),
    })),
  };
}

/**
 * Generate a list of questions matching the requested filter.
 *
 * @param {Object}  opts
 * @param {string}  opts.type   'single' | 'multi' | 'mixed'
 * @param {number}  opts.count  number of questions to generate
 * @param {number}  [opts.seed] optional seed for reproducible shuffles
 * @returns {Array} questions shaped for the assessment store
 */
export function generateQuestions({ type, count, seed }) {
  const want = Math.max(1, Math.floor(count) || 0);
  const bank = flatBank();

  let pool;
  if (type === 'single') pool = bank.filter(q => q.type === 'single');
  else if (type === 'multi') pool = bank.filter(q => q.type === 'multi');
  else pool = bank;

  if (pool.length === 0) return [];

  const shuffled = shuffle(pool, seed);
  const picked = [];
  let i = 0;
  // Recycle from the start if the user asks for more than the bank holds.
  while (picked.length < want) {
    picked.push(shuffled[i % shuffled.length]);
    i++;
    if (i > 5000) break; // hard safety
  }
  return picked.map(toAssessmentQuestion);
}

/**
 * Convenience wrapper — generates a full assessment patch (title +
 * questions) ready to be applied via UPDATE_ASSESSMENT + IMPORT_QUESTIONS.
 */
export function generatePracticeTest({ name, count, type }) {
  const questions = generateQuestions({ type, count });
  return {
    title: (name && name.trim()) || 'AI-generated practice test',
    questions,
  };
}
